import React, { useState, useRef } from "react";
import {
  CheckCircle,
  Star,
  MapPin,
  Building,
  Activity,
  ShoppingBag,
  Train,
  Sparkles,
} from "lucide-react";
import { Card, Modal } from "antd";
import { Button } from "./ui/button";
import InquirySidebar from "./inquiry-sidebar";
import { ServiceData } from "../types/service";
import { t } from "i18next";
import { InquirySidebarResort } from "./inquiry-sidebar_resort";

const amenityIcons: { [key: string]: React.ElementType } = {
  Spa: Sparkles,
  "Swimming Pool": Building,
  Gym: Activity,
  Jacuzzi: Sparkles,
  "Night Club": Sparkles,
};

interface ResortDetailsCardProps {
  hotel: any;
  service: ServiceData;
  services: ServiceData[];
  selectedVisa: ServiceData | undefined;
  highlightSidebar: boolean;
  setSelectedVisa?: (service: ServiceData) => void; // ✅ added
}

/**
 * ResortDetailsCard
 * Displays detailed resort information with sidebar and amenities.
 */
export function ResortDetailsCard({
  hotel,
  service,
  services,
  selectedVisa,
  highlightSidebar,
  setSelectedVisa, // ✅ added
}: ResortDetailsCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const primaryRoom = hotel.rooms?.[0];
  const primaryBookingOption = primaryRoom?.bookingOptions?.[0];

  const fee = (Number(service?.price) || 0) + (Number(service?.centerFee) || 0);

  const handleApplyNow = () => {
    if (setSelectedVisa) setSelectedVisa(service); // ✅ update selected visa

    const element = document.getElementById("sideForm");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });

      // Wait for scroll animation to finish before focusing
      setTimeout(() => {
        const input = document.getElementById(
          "customerNameInput"
        ) as HTMLInputElement;
        const inputKsa = document.getElementById(
          "customerNameInput1"
        ) as HTMLInputElement;
        input?.focus();
        inputKsa?.focus();
      }, 0);
    }
  };

  return (
      <Card className="bg-gray-50/70 border-gray-200 shadow-sm rounded-xl overflow-hidden flex flex-col h-full">
        {/* 🏨 Header */}
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-gray-800">
            {service?.name || hotel.name}
          </h1>
          {hotel.rating && (
            <div className="flex items-center">
              {[...Array(hotel.rating)].map((_, i) => (
                <Star
                  key={i}
                  className="w-5 h-5 text-yellow-400 fill-current"
                />
              ))}
            </div>
          )}
        </div>

        {/* 🔹 Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* 🖼️ Image Section */}
            <div className="grid grid-cols-3 gap-2">
              {/* Main Image */}
              <div className="col-span-2 relative aspect-[4/3] rounded-lg overflow-hidden group">
                <img
                  src={hotel.images.main?.imageUrl}
                  alt={hotel.images.main?.description}
                  className="object-cover w-full h-full"
                />
                <div className="absolute bottom-4 left-4 bg-black/50 text-white text-sm px-3 py-1 rounded-md">
                  +86 Property Photos
                </div>
              </div>

              {/* Side Images */}
              <div className="col-span-1 flex flex-col gap-2">
                <img
                  src={hotel.images.room?.imageUrl}
                  alt={hotel.images.room?.description}
                  className="object-cover w-full h-full rounded-lg"
                />
                <img
                  src={hotel.images.guest?.imageUrl}
                  alt={hotel.images.guest?.description}
                  className="object-cover w-full h-full rounded-lg"
                />
              </div>
            </div>

            {/* 🏠 About Section */}
            <div>
              <h2 className="text-xl font-bold mb-2">About Property</h2>
              <p className="text-gray-600">
                {hotel.property.description}{" "}
                <a href="#" className="text-primary hover:underline">
                  More
                </a>
              </p>
            </div>

            {/* 📍 Why Book Section */}
            <div>
              <h3 className="text-lg font-bold mb-4">
                Why book this property?
              </h3>
              <div className="space-y-4">
                {/* Attractions */}
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-gray-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Top Nearby Attractions</h4>
                    <p className="text-sm text-gray-600">
                      {hotel.nearby.attractions}
                    </p>
                  </div>
                </div>

                {/* Shopping */}
                <div className="flex items-start gap-4">
                  <ShoppingBag className="w-5 h-5 text-gray-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Nearby Shopping Hubs</h4>
                    <p className="text-sm text-gray-600">
                      {hotel.nearby.shopping}
                    </p>
                  </div>
                </div>

                {/* Transport */}
                <div className="flex items-start gap-4">
                  <Train className="w-5 h-5 text-gray-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Near Public Transport</h4>
                    <p className="text-sm text-gray-600">
                      {hotel.nearby.transport}
                    </p>
                  </div>
                </div>
              </div>

              {/* 🧴 Amenities */}
              <h3 className="text-lg font-bold mt-6 mb-2">Amenities</h3>
              <p className="text-sm text-gray-500 mb-4">
                Amenities rated {hotel.review.score} by guests
              </p>

              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {hotel.amenities.map((amenity: string, index: number) => {
                  const Icon = amenityIcons[amenity] || Sparkles;
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-gray-700"
                    >
                      <Icon className="w-5 h-5" />
                      <span>{amenity}</span>
                    </div>
                  );
                })}
                <button
                  onClick={openModal}
                  className="text-primary font-semibold hover:underline"
                >
                  + More Amenities
                </button>
              </div>

              <div className="space-y-3 py-4">
                <div className="flex justify-start items-center gap-6">
                  {/* Price and AED */}
                  <p className="font-bold text-primary text-2xl md:text-3xl mt-0">
                    {t("AED")} {fee.toLocaleString()}
                  </p>

                  {/* Button */}
                  <div className="mt-0">
                    <Button
                      onClick={handleApplyNow} // ✅ updated
                      className="bg-primary hover:bg-primary/90 text-base px-4 py-2"
                      size="sm"
                    >
                      {t("Apply Now")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right (Sidebar) */}
          <div id="sideForm" className="space-y-4" ref={sidebarRef}>
            <div className="sticky top-25 gsap-ksa-sidebar">
              <InquirySidebar
                visaOptions={services}
                selectedVisa={selectedVisa}
                highlight={highlightSidebar}
              />
              {/* <InquirySidebarResort
                visaOptions={services}
                selectedVisa={selectedVisa}
                highlight={highlightSidebar}
              /> */}
            </div>
          </div>
        </div>

        {/* 💬 Amenities Modal */}
        <Modal
          title="All Amenities"
          open={isModalOpen}
          onCancel={closeModal}
          footer={[
            <Button key="close" onClick={closeModal}>
              Close
            </Button>,
          ]}
        >
          <ul className="space-y-2 text-gray-700">
            {hotel.amenities.map((a: string, i: number) => (
              <li key={i}>• {a}</li>
            ))}
          </ul>
        </Modal>
      </Card>
  );
}
